import { useState } from 'react';

import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';
import { WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useQueryGetCrossVersionsTrends } from 'api/queries/getCrossVersionsTrends.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import { SingleLineOptions } from 'components/widgets/Trends/components/TrendsGraph/TrendsGraph';

export const useTrendsProvider = ({
    widgetId,
    contractId,
    projectId,
    latestVersionId,
    globalFilterData,
    localFilters,
    widgetViewMode,
    componentKey,
    endpoints,
}) => {
    const { isGenerativeAi, reportId } = usePinboardWidgetWrapperContext();
    const [singleLine, setSingleLine] = useState<SingleLineOptions>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode)
            ? localFilters.singleLine
            : 'Single Parameter (Actual value)',
    );
    const [selectedOptions, setSelectedOptions] = useState<ISelectOption<string> | ISelectOption<string>[]>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters.selectedOptions : [],
    );

    const [toggleLabels, setToggleLabels] = useState<boolean>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters.toggleLabels : false,
    );

    const source = widgetId ? localFilters : { ...globalFilterData, singleLine, selectedOptions, toggleLabels };

    const payload = {
        ids: [latestVersionId],
        filter: {
            filters: getFiltersData({
                [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                    ?.filter((item) => !item?.isExclude)
                    .map((i) => i?.name),
                [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                    ?.filter((item) => item?.isExclude)
                    .map((i) => i?.name),
                [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
                [FilterNames.TAG_CARD_BOARD]: source?.tags?.filter((item) => !item?.isExclude).map((i) => i?.name),
                [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags
                    ?.filter((item) => item?.isExclude)
                    .map((i) => i?.name),
                [FilterNames.WBS_SEARCH]: source?.wbs,
            }),
        },
        ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId, reportId),
    };

    const pillsData = {
        activityCodes: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.isExclude).map((i) => i.name),
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
        tagsExclude: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
        wbs: source?.wbs,
        show: source?.singleLine,
        parameters: Array.isArray(source?.selectedOptions)
            ? source?.selectedOptions.map((i) => i.label)
            : source?.selectedOptions?.label,
    };

    const { pillsList: pills } = useGlobalFiltersHook({ source: pillsData, latestVersionId, widgetId });

    return {
        ui: {
            state: {
                projectId,
                contractId,
                latestVersionId,
                activityCodes: source?.activityCodes,
                milestoneFragnet: source?.milestoneFragnet,
                tags: source?.tags,
                wbs: source?.wbs,
                singleLine: source?.singleLine,
                selectedOptions: source?.selectedOptions,
                toggleLabels: source?.toggleLabels,
            },
            setters: {
                setSingleLine,
                setSelectedOptions,
                setToggleLabels,
            },
            pills,
        },
        api: {
            crossVersionsTrends: {
                instance: useQueryGetCrossVersionsTrends(payload, endpoints.getCrossVersionsTrends),
                payload: payload,
                endpoint: endpoints.getCrossVersionsTrends,
            },
        },
    };
};
